import {
  AppBar,
  Box,
  Button,
  Dialog,
  Paper,
  DialogContent,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  SsidChart as DownloadECGIcon,
  SignalCellularAlt as DownloadParamsIcon,
} from "@mui/icons-material";
import { useGetPatientReports, useGetReportById } from "../useReportQueries";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import { useEffect, useRef, useState } from "react";
import { useGetPatientById } from "../../PatientComponents";
import CanvasJSReact from "@canvasjs/react-charts";
import {
  prettify,
  renderEUDate,
  renderEUDateTime,
  renderDateUTC,
  convertServerToLocalDatetime,
} from "../../../utils/utils";
import axios from "axios";
import { saveAs } from "file-saver";
import { normalizeAmplitude, getECGLayout } from "./reportUtils/ecgReport";

function ECGReport({ report_id, patient_id, closeModal }) {
  var CanvasJSChart = CanvasJSReact.CanvasJSChart;

  // === web service queries/mutations ===
  const reportQuery = useGetReportById(report_id);
  const patientQuery = useGetPatientById(patient_id);

  // === report display ===
  const [layouts, setLayouts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const saveFile = (url, filename) => {
    saveAs(url, filename);
  };

  useEffect(() => {
    // parse csv
    if (!loaded && reportQuery.isSuccess) {
      axios.get(reportQuery.data.data.ECGRaw).then((res) => {
        // parsing the remote file
        let stringa = JSON.stringify(res.data);
        if (reportQuery.data.origin === "viatom_bp2") {
          stringa = stringa.replace("[", "").replace("]", "");
        } else {
          stringa = stringa.replace("b'", "").replace("'", "");
          stringa = stringa.replaceAll("\\\\n", "\n");
        }

        const arrayOfMeasures =
          reportQuery.data.origin === "viatom_bp2"
            ? stringa.split(",")
            : stringa.split("\n");
        const records = arrayOfMeasures;

        let traceECGs = [];
        const secondi_per_riga = 8;
        const freq_rilevazione =
          reportQuery.data.origin === "viatom_bp2" ? 125 : 200; // 512
        const totale_punti_riga = secondi_per_riga * freq_rilevazione;

        // TMP fix - butterflife still misses a couple of seconds
        const nrounds =
          reportQuery.data.origin === "viatom_bp2"
            ? Math.ceil(records.length / totale_punti_riga)
            : Math.floor(records.length / totale_punti_riga);
        let layouts = [];

        // get layout for each ecg round
        for (let round = 0; round < nrounds; round++) {
          let traceECG = [];
          for (let i = 0; i < totale_punti_riga; i++) {
            if (
              reportQuery.data.origin === "viatom_bp2" &&
              records[i + round * totale_punti_riga] === undefined
            )
              break;
            // if (records[i + round * totale_punti_riga] === undefined) break;

            traceECG.push({
              x: i,
              y:
                reportQuery.data.origin === "viatom_bp2"
                  ? parseInt(records[i + round * totale_punti_riga] * 100)
                  : parseInt(
                      records[i + round * totale_punti_riga].split(";")[2]
                    ),
            });
          }
          traceECGs.push(traceECG);

          const normalized = normalizeAmplitude(traceECG);
          const layout = getECGLayout(normalized, freq_rilevazione);

          layout["ecg_index"] = round;
          layouts.push(layout);
        }
        setLayouts(layouts);
        setLoaded(true);
      });
    }
  }, [reportQuery.status]);

  return (
    <>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeModal}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent>
        {reportQuery.isLoading || !loaded ? (
          <LoadingPaper />
        ) : reportQuery.isError ? (
          <LoadingError error={reportQuery.error} />
        ) : (
          <Stack spacing={2} direction={"column"}>
            <Paper sx={{ padding: "10px" }}>
              <Box sx={{ width: "100%" }}>
                <Box sx={{ float: "left" }}>
                  <Typography fontWeight={"bold"} variant="h5">
                    Grafico ECG
                  </Typography>
                </Box>
                {"Params" in reportQuery.data.data && (
                  <Box sx={{ float: "right" }}>
                    <Stack direction={"row"} gap={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          saveFile(
                            reportQuery.data.data.ECG,
                            `${patientQuery.data.first_name}_${patientQuery.data.last_name}_${reportQuery.data.date}_ecg.pdf`
                          );
                        }}
                      >
                        <DownloadECGIcon />
                        Scarica ECG
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          saveFile(
                            reportQuery.data.data.Params,
                            `${patientQuery.data.first_name}_${patientQuery.data.last_name}_${reportQuery.data.date}_parametri.pdf`
                          );
                        }}
                      >
                        <DownloadParamsIcon />
                        Scarica Parametri
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>

              <Grid container>
                <Grid item xs={6}>
                  <Typography>
                    Assitito:{" "}
                    {patientQuery.isLoading
                      ? ""
                      : `${patientQuery.data.first_name} ${patientQuery.data.last_name}`}
                  </Typography>
                  <Typography>
                    Dispositivo: {prettify(reportQuery.data.origin)}
                  </Typography>
                  <Typography>
                    Data misurazione:
                    {renderEUDateTime(
                      convertServerToLocalDatetime(reportQuery.data.data.Date)
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {/* <Typography>
                    Durata: {reportQuery.data.data.Duration}
                  </Typography> */}
                  {reportQuery.data.data.HR !== null && (
                    <Typography>
                      Frequenza Cardiaca: {reportQuery.data.data.HR}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
            {reportQuery.isLoading ? (
              <LoadingPaper />
            ) : reportQuery.isError ? (
              <LoadingError error={reportQuery.error} />
            ) : (
              <Paper sx={{ padding: "10px" }}>
                {layouts !== undefined &&
                  loaded &&
                  layouts.map((layout) => (
                    <CanvasJSChart key={crypto.randomUUID()} options={layout} />
                  ))}
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
    </>
  );
}

export default ECGReport;
